import _ from 'lodash';
import { useState } from 'react';
import { TextFormatterPopover } from '@/core/TextFormatterPopover.molecule';
import type { TableColumnFilter } from '@/core/tableUtilities/tables';
import { TableFilterPopover } from '@/core/tableUtilities/TableFilterPopover.organism';
import { TableColumnMenu } from '@/core/tableUtilities/TableColumnMenu.organism';
import type { AgGridAggregationFunction } from '@/tableBuilder/tableBuilder.constants';
import { AgGridAggregationFunctions, TABLE_BUILDER, TextHeaderMenuAction } from '@/tableBuilder/tableBuilder.constants';
import { doTrack } from '@/track/track.service';
import { priorityColors } from '@/services/systemConfiguration.utilities';
import type { AgGridHeaderProps, TableTextFormatter } from '@/tableBuilder/tableBuilder.types';
import type { ColumnAction } from '@/core/tableUtilities/tableUtilities.types';

export interface TextHeaderMenuProps extends Pick<AgGridHeaderProps, 'autoSizeColumn' | 'autoSizeAllColumns'> {
  canRenameHeader: boolean;
  columnKey: string;
  columnBackgroundColor?: string;
  columnTextAlign?: string;
  columnTextColor?: string;
  columnTextStyle?: string[];
  headerBackgroundColor?: string;
  headerTextAlign?: string;
  headerTextColor?: string;
  headerTextStyle?: string[];
  isTransposed: boolean;
  textFormatter: TableTextFormatter;
  editHeaderValue: () => void;
  removeColumn: (key: string) => void;
  actions?: TextHeaderMenuAction[];
  setColumnFilter?: (key: string, filter: TableColumnFilter) => void;
  columnFilter?: TableColumnFilter;
  isFilterDisabled?: boolean;
  filterHelpText?: string;
  canSort?: boolean;
  canSortDisabledTooltip?: string;
  sortDirection?: string;
  sortByColumn: (key: string, direction?: string) => void;
  isStringColumn?: boolean;
  isDurationColumn?: boolean;
  fetchStringColumnValues?: (columnKey: string, isStringColumn: boolean, cancellationGroup: string) => void;
  distinctStringValues?: string[];
  thresholds?: any[] | undefined;
  groupByColumn?: (columnKey: string) => void;
  isRemoveSortDisabled?: boolean;
  isInteractiveContent?: boolean;
  aggregationFunction?: AgGridAggregationFunction;
  setAggregationFunction?: (columnKey: string, aggregationFunction: string) => void;
}

const DATA_CANCELLATION_GROUP = 'tableBuilder';

export const TableBuilderTextHeaderMenu: React.FunctionComponent<TextHeaderMenuProps> = ({
  canRenameHeader,
  columnKey,
  columnBackgroundColor,
  columnTextAlign,
  columnTextColor,
  columnTextStyle,
  headerBackgroundColor,
  headerTextAlign,
  headerTextColor,
  headerTextStyle,
  isTransposed,
  textFormatter,
  editHeaderValue,
  removeColumn,
  actions = _.values(TextHeaderMenuAction),
  setColumnFilter,
  columnFilter = undefined,
  isFilterDisabled = false,
  filterHelpText = undefined,
  canSort = false,
  canSortDisabledTooltip,
  sortByColumn,
  sortDirection = undefined,
  isStringColumn = false,
  isDurationColumn = false,
  fetchStringColumnValues = _.noop,
  distinctStringValues = undefined,
  thresholds = undefined,
  autoSizeColumn,
  autoSizeAllColumns,
  groupByColumn,
  isInteractiveContent,
  aggregationFunction,
  setAggregationFunction,
}) => {
  const priorities = priorityColors();
  const colors: string[] = _.chain(priorities).map('color').uniq().value();

  const [showRowFormatPopover, setShowRowFormatPopover] = useState(false);
  const [showColumnFormatPopover, setShowColumnFormatPopover] = useState(false);
  const [showHeaderFormatPopover, setShowHeaderFormatPopover] = useState(false);
  const [showColumnFilterPopover, setShowColumnFilterPopover] = useState(false);

  const filterColumn = () => {
    setShowColumnFilterPopover(true);
    fetchStringColumnValues(columnKey, isStringColumn, DATA_CANCELLATION_GROUP);
  };

  const columnActions: ColumnAction<TextHeaderMenuAction>[] = _.filter(
    [
      {
        iconClass: 'fa-filter',
        translationKey: 'FILTER.FILTER',
        actionKey: TextHeaderMenuAction.Filter,
        action: () => filterColumn(),
        disabled: isFilterDisabled,
        tooltip: !isFilterDisabled ? undefined : 'TABLE_BUILDER.ONLY_ON_TABLE_WITH_ITEMS_SAME_TYPE',
      },
      {
        actionKey: TextHeaderMenuAction.Sort,
        iconClass: 'fa-sort',
        translationKey: 'TABLE_BUILDER.SORT',
        disabled: !canSort,
        tooltip: canSortDisabledTooltip,
        subMenuItems: [
          {
            disabled: sortDirection === 'asc',
            iconClass: 'fa-arrow-down-short-wide',
            translationKey: 'TABLE_BUILDER.SORT_ASCENDING',
            action: () => {
              sortByColumn(columnKey, 'asc');
              doTrack(TABLE_BUILDER, 'sort', 'enabled');
            },
          },
          {
            disabled: sortDirection === 'desc',
            iconClass: 'fa-arrow-down-wide-short',
            translationKey: 'TABLE_BUILDER.SORT_DESCENDING',
            action: () => {
              sortByColumn(columnKey, 'desc');
              doTrack(TABLE_BUILDER, 'sort', 'enabled');
            },
          },
          {
            disabled: _.isUndefined(sortDirection),
            iconClass: 'fa-times',
            translationKey: 'TABLE_BUILDER.SORT_REMOVE',
            action: () => {
              sortByColumn(columnKey);
              doTrack(TABLE_BUILDER, 'sort', 'disabled');
            },
          },
        ],
      },
      {
        iconClass: 'fa-diagram-subtask',
        translationKey: 'TABLE_BUILDER.GROUP_BY_COLUMN',
        actionKey: TextHeaderMenuAction.GroupByColumn,
        action: () => groupByColumn?.(columnKey),
      },
      {
        iconClass: 'fa-sigma',
        translationKey: 'TABLE_BUILDER.AGGREGATION.TITLE',
        actionKey: TextHeaderMenuAction.AggregateByColumn,
        subMenuItems: AgGridAggregationFunctions.map((agg) => ({
          iconClass: (!aggregationFunction && agg === 'none') || aggregationFunction === agg ? 'fa-check' : '',
          translationKey: `TABLE_BUILDER.AGGREGATION.${agg.toUpperCase()}`,
          action: () => {
            setAggregationFunction?.(columnKey, agg);
          },
        })),
      },
      {
        iconClass: 'fa-rectangle-list',
        translationKey: 'TABLE_BUILDER.HEADER_PROPERTIES',
        actionKey: TextHeaderMenuAction.HeaderStyle,
        action: () => setShowHeaderFormatPopover(true),
      },
      {
        iconClass: 'fa-pen-paintbrush',
        translationKey: isTransposed ? 'TABLE_BUILDER.ROW_PROPERTIES' : 'TABLE_BUILDER.COLUMN_PROPERTIES',
        actionKey: TextHeaderMenuAction.RowStyle,
        action: () => setShowRowFormatPopover(true),
      },
      {
        iconClass: 'fa-pen-paintbrush',
        translationKey: isTransposed ? 'TABLE_BUILDER.COLUMN_PROPERTIES' : 'TABLE_BUILDER.ROW_PROPERTIES',
        actionKey: TextHeaderMenuAction.ColumnStyle,
        action: () => setShowColumnFormatPopover(true),
      },
      {
        iconClass: 'fc-paintbrush',
        translationKey: 'TABLE_BUILDER.APPLY_STYLE_TO_ALL',
        actionKey: TextHeaderMenuAction.HeaderStyle,
        subMenuItems: [
          {
            iconClass: 'fa-rectangle-list',
            translationKey: 'TABLE_BUILDER.HEADERS',
            action: () => textFormatter.setStyleToAllHeaders(columnKey),
          },
          {
            iconClass: 'fa-columns',
            translationKey: isTransposed ? 'TABLE_BUILDER.ROWS' : 'TABLE_BUILDER.COLUMNS',
            action: () => textFormatter.setStyleToAllColumns(columnKey),
          },
          {
            iconClass: 'fa-table',
            translationKey: isTransposed ? 'TABLE_BUILDER.ROWS_AND_HEADERS' : 'TABLE_BUILDER.COLUMNS_AND_HEADERS',
            action: () => textFormatter.setStyleToAllHeadersAndColumns(columnKey),
          },
        ],
      },
      {
        iconClass: 'fa-copy',
        translationKey: 'TABLE_BUILDER.COPY_STYLE',
        actionKey: TextHeaderMenuAction.HeaderStyle,
        action: () => textFormatter.copyStyle(columnKey),
      },
      {
        iconClass: 'fc-paintbrush',
        translationKey: 'TABLE_BUILDER.PASTE_STYLE_OF',
        actionKey: TextHeaderMenuAction.HeaderStyle,
        subMenuItems: [
          {
            iconClass: 'fa-rectangle-list',
            translationKey: 'TABLE_BUILDER.HEADER',
            action: () => textFormatter.pasteStyleOnHeader(columnKey),
          },
          {
            iconClass: 'fa-columns',
            translationKey: isTransposed ? 'TABLE_BUILDER.ROW' : 'TABLE_BUILDER.COLUMN',
            action: () => textFormatter.pasteStyleOnColumn(columnKey),
          },
          {
            iconClass: 'fa-table',
            translationKey: isTransposed ? 'TABLE_BUILDER.ROW_AND_HEADER' : 'TABLE_BUILDER.COLUMN_AND_HEADER',
            action: () => textFormatter.pasteStyleOnHeaderAndColumn(columnKey),
          },
        ],
      },
      {
        iconClass: 'fa-pencil',
        translationKey: 'TABLE_BUILDER.RENAME_HEADER',
        actionKey: TextHeaderMenuAction.Rename,
        action: () => editHeaderValue(),
        disabled: !canRenameHeader,
      },
      {
        iconClass: 'fc-autosize-cell2 fa-sm',
        translationKey: 'TABLE_BUILDER.AUTO_SIZE_COLUMN',
        actionKey: TextHeaderMenuAction.AutoSize,
        action: autoSizeColumn,
      },
      {
        iconClass: 'fc-autosize-all-cells fa-sm',
        translationKey: 'TABLE_BUILDER.AUTO_SIZE_ALL_COLUMNS',
        actionKey: TextHeaderMenuAction.AutoSizeAll,
        action: autoSizeAllColumns,
      },
      {
        iconClass: 'fa-times',
        translationKey: 'REMOVE_COLUMN',
        actionKey: TextHeaderMenuAction.Remove,
        action: () => removeColumn(columnKey),
      },
    ],
    ({ actionKey }) => _.includes(actions, actionKey),
  );

  return (
    <div>
      <TableColumnMenu columnActions={columnActions} columnKey={columnKey} />
      <TextFormatterPopover
        backgroundColor={headerBackgroundColor!}
        textAlign={headerTextAlign!}
        textColor={headerTextColor!}
        textStyle={headerTextStyle!}
        colors={colors}
        columnKey={columnKey}
        onClose={() => setShowHeaderFormatPopover(false)}
        setTextAlign={(columnKey, alignment) => textFormatter.setHeaderTextAlign(columnKey, alignment)}
        setTextBackground={(columnKey, color) => textFormatter.setHeaderBackground(columnKey, color)}
        setTextColor={(columnKey, color) => textFormatter.setHeaderTextColor(columnKey, color)}
        setTextStyle={(columnKey, styles) => textFormatter.setHeaderTextStyle(columnKey, styles)}
        placement="bottom"
        target={<div className="ml16" />}
        show={showHeaderFormatPopover}
        title="TABLE_BUILDER.HEADER_PROPERTIES"
      />
      <TextFormatterPopover
        backgroundColor={columnBackgroundColor!}
        textAlign={columnTextAlign!}
        textColor={columnTextColor!}
        textStyle={columnTextStyle!}
        colors={colors}
        columnKey={columnKey}
        onClose={() => setShowRowFormatPopover(false)}
        setTextAlign={(columnKey, alignment) => textFormatter.setColumnTextAlign(columnKey, alignment)}
        setTextBackground={(columnKey, color) => textFormatter.setColumnBackground(columnKey, color)}
        setTextColor={(columnKey, color) => textFormatter.setColumnTextColor(columnKey, color)}
        setTextStyle={(columnKey, styles) => textFormatter.setColumnTextStyle(columnKey, styles)}
        placement="bottom"
        target={<div className="ml16" />}
        show={showRowFormatPopover}
        title={isTransposed ? 'TABLE_BUILDER.ROW_PROPERTIES' : 'TABLE_BUILDER.COLUMN_PROPERTIES'}
      />
      <TextFormatterPopover
        backgroundColor={columnBackgroundColor!}
        textAlign={columnTextAlign!}
        textColor={columnTextColor!}
        textStyle={columnTextStyle!}
        colors={colors}
        columnKey={columnKey}
        onClose={() => setShowColumnFormatPopover(false)}
        setTextAlign={(columnKey, alignment) => textFormatter.setColumnTextAlign(columnKey, alignment)}
        setTextStyle={(columnKey, styles) => textFormatter.setColumnTextStyle(columnKey, styles)}
        placement="bottom"
        target={<div className="ml16" />}
        show={showColumnFormatPopover}
        title={isTransposed ? 'TABLE_BUILDER.COLUMN_PROPERTIES' : 'TABLE_BUILDER.ROW_PROPERTIES'}
      />
      <TableFilterPopover
        columnFilter={columnFilter}
        columnKey={columnKey}
        notifyOnClose={() => setShowColumnFilterPopover(false)}
        setColumnFilter={(columnKey, filter) => setColumnFilter?.(columnKey, filter as any)}
        isStringColumn={isStringColumn}
        isDurationColumn={isDurationColumn}
        distinctStringValues={distinctStringValues}
        thresholds={thresholds}
        helpText={filterHelpText}
        placement="bottom"
        show={_.includes(actions, TextHeaderMenuAction.Filter) && showColumnFilterPopover}
      />
    </div>
  );
};
